// format.ts — number and sha formatting shared by the graph rows, the
// commit-overview metrics table and the feed charts, so one metric value
// never renders two different ways on the same screen.

// Significant-digit-aware fixed formatting: enough precision to tell two
// neighbouring metric values apart, never so much that a graph row's
// combo-button overflows.
export function formatNum(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(0);
  if (abs >= 100) return v.toFixed(1);
  if (abs >= 10) return v.toFixed(2);
  if (abs >= 1) return v.toFixed(3);
  return v.toPrecision(3);
}

// Chart-axis / summary-table formatting: exponential for the very small and
// the very large (learning rates, step counts), plain otherwise.
export function formatChartNum(v: number | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a !== 0 && (a < 1e-3 || a >= 1e6)) return v.toExponential(3);
  return Number(v.toPrecision(5)).toString();
}

// Coarse "how long ago" for list rows: one unit, no decimals ("3h ago",
// "2d ago"). A row that already carries a slug, three counts and a status
// badge has no width for a timestamp, and the question a reader asks of a
// list sorted by recency is "how stale is this", not "which second".
// Returns '' for a missing or unparseable instant so the caller can leave
// the stamp out rather than print a placeholder.
export function relativeTime(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.round((now - then) / 1000);
  // Clock skew between the server and this browser reads as the future;
  // "just now" is the honest answer, never "-2m ago".
  const units: [number, string][] = [
    [31536000, 'y'],
    [2592000, 'mo'],
    [86400, 'd'],
    [3600, 'h'],
    [60, 'm'],
  ];
  for (const [span, suffix] of units) {
    if (secs >= span) return `${Math.floor(secs / span)}${suffix} ago`;
  }
  return 'just now';
}

// The same instant in full, for the row's title tooltip.
export function absoluteTime(iso: string | undefined): string {
  if (!iso) return '';
  const t = new Date(iso);
  return Number.isFinite(t.getTime()) ? t.toLocaleString() : '';
}

export function shortSha(sha: string, n = 8): string {
  return sha.slice(0, n);
}

// Subject line of a commit message, with a stand-in when the webhook has
// not ingested the message yet.
export function subjectOf(message: string | undefined, fallback = '(no message)'): string {
  const first = (message || '').split('\n')[0].trim();
  return first || fallback;
}
